import React, { useState, useEffect } from 'react';
import { GridToolbarQuickFilter } from '@mui/x-data-grid';
import { Button, Box } from '@mui/material';
import DownloadIcon from '@mui/icons-material/Download';
import * as XLSX from 'xlsx';
import { toast } from 'react-toastify';
import useDebounce from '../common/useDebounce';
 
//  Common export toolbar
//  dateFields: pass field names that are dates (ex: ['grnDate','approvedOn'])
const CustomDataGridToolbarServer = ({
  columns,
  onFetchData,
  fileName,
  name,
  onSearch,
  dateFields = [],
}) => {
 
  // API date like: "2025-11-06T00:00:00"
  // Convert to Excel date serial number so it works in Excel + Libre/OpenOffice Calc
  const toExcelDateSerial = (value) => {
    if (!value) return '';
 
    const m = String(value).trim().match(/^(\d{4})-(\d{2})-(\d{2})/);
    if (!m) return '';
 
    const y = Number(m[1]);
    const mo = Number(m[2]) - 1;
    const d = Number(m[3]);
 
    // Use UTC midnight (prevents +/- 1 day shift)
    const utcMs = Date.UTC(y, mo, d);
 
    // Excel epoch in UTC: 1899-12-30
    const excelEpochUtcMs = Date.UTC(1899, 11, 30);
 
    const serial = (utcMs - excelEpochUtcMs) / 86400000;
 
    return Math.round(serial);
  };
 
  // If it doesn't look like a number, return original as-is
  const toExcelNumber = (v) => {
    if (v === null || v === undefined || v === '') return '';
    if (typeof v === 'number') return v;
 
    const s = String(v).trim();
 
    // If it contains letters (except e/E for scientific), treat as text
    // (prevents converting things like "INV-00460" into NaN)
    if (/[a-df-zA-DF-Z]/.test(s)) return v;
 
    // remove commas/currency/spaces
    const cleaned = s.replace(/[,₹$]/g, '').replace(/\s+/g, '');
    const n = Number(cleaned);
 
    return Number.isFinite(n) ? n : v;
  };
 
  // Decide numeric columns dynamically by sampling first N rows
  // A column is numeric if 70%+ of its non-empty sampled values are numeric-like
  const buildNumericHeaderSet = (apiData, displayedColumns, sampleSize = 25) => {
    const headersNumeric = new Set();
    const data = Array.isArray(apiData) ? apiData : [];
    const N = Math.min(sampleSize, data.length);
 
    displayedColumns.forEach((column) => {
      if (!column?.field) return;
      if (column.field === 'actions') return;
      if (dateFields.includes(column.field)) return; // dates handled separately
 
      let numericCount = 0;
      let nonEmptyCount = 0;
 
      for (let i = 0; i < N; i++) {
        const row = data[i];
 
        const raw = column.valueGetter
          ? column.valueGetter({ row, value: row?.[column.field] })
          : row?.[column.field];
 
        if (raw === null || raw === undefined || raw === '') continue;
 
        nonEmptyCount++;
        const n = toExcelNumber(raw);
        if (typeof n === 'number') numericCount++;
      }
 
      // Mark numeric if we have at least 3 non-empty samples and >=70% numeric-like
      if (nonEmptyCount >= 3 && numericCount / nonEmptyCount >= 0.7) {
        headersNumeric.add(column.headerName);
      }
    });
 
    return headersNumeric;
  };
 
  const handleExportExcel = async () => {
    try {
      const apiData = await onFetchData();
 
      if (!apiData || apiData.length === 0) {
        toast.error('No data to export');
        return;
      }
 
      const displayedColumns = columns.filter(
        (column) => column.hide !== true && column.field !== 'actions'
      );
 
      // Infer numeric columns dynamically (no hardcoding)
      const numericHeaders = buildNumericHeaderSet(apiData, displayedColumns);
 
      const worksheetData = apiData.map((row) => {
        const data = {};
        displayedColumns.forEach((column) => {
          const raw = column.valueGetter
            ? column.valueGetter({ row, value: row?.[column.field] })
            : row?.[column.field];
 
          // Convert specified date fields
          if (dateFields.includes(column.field)) {
            data[column.headerName] = toExcelDateSerial(raw);
          }
          // Convert inferred numeric columns
          else if (numericHeaders.has(column.headerName)) {
            data[column.headerName] = toExcelNumber(raw);
          }
          // Keep as-is
          else {
            data[column.headerName] = raw ?? '';
          }
        });
        return data;
      });
 
      const ws = XLSX.utils.json_to_sheet(worksheetData);
 
      // Apply dd/mm/yyyy format to date columns (by header name)
      const dateHeaders = new Set(
        displayedColumns
          .filter((c) => dateFields.includes(c.field))
          .map((c) => c.headerName)
      );
 
      // Find header row index (json_to_sheet uses row 1 as header row => index 0)
      const range = XLSX.utils.decode_range(ws['!ref'] || 'A1:A1');
 
      // map column index => header text
      const headerMap = {};
      for (let C = range.s.c; C <= range.e.c; C++) {
        const addr = XLSX.utils.encode_cell({ r: 0, c: C });
        const cell = ws[addr];
        headerMap[C] = cell ? String(cell.v) : '';
      }
 
      // Force correct cell types:
      // - date columns => numeric + date format
      // - inferred numeric columns => numeric
      for (let R = 1; R <= range.e.r; R++) {
        for (let C = range.s.c; C <= range.e.c; C++) {
          const addr = XLSX.utils.encode_cell({ r: R, c: C });
          const cell = ws[addr];
          if (!cell) continue;
 
          const header = headerMap[C];
 
          // Date formatting
          if (dateHeaders.has(header)) {
            cell.t = 'n';
            cell.z = 'dd/mm/yyyy';
            continue;
          }
 
          // Numeric formatting
          if (numericHeaders.has(header)) {
            const n = toExcelNumber(cell.v);
            if (typeof n === 'number') {
              cell.v = n;
              cell.t = 'n';
            }
          }
        }
      }
 
      const wb = XLSX.utils.book_new();
      XLSX.utils.book_append_sheet(wb, ws, 'Data');
      XLSX.writeFile(wb, fileName);
 
    } catch (error) {
      console.error(error);
      toast.error('Error exporting data');
    }
  };
 
  const [searchInput, setSearchInput] = useState('');
  const debouncedSearchInput = useDebounce(searchInput, 1000);
 
  useEffect(() => {
    onSearch(debouncedSearchInput);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [debouncedSearchInput]);
 
  const handleChange = (event) => setSearchInput(event.target.value);
 
  return (
    <>
      <Box sx={{ p: 0.5, pb: 0, float: 'right' }}>
        <GridToolbarQuickFilter
          quickFilterParser={(v) => [v]}
          onChange={handleChange}
          value={searchInput}
        />
      </Box>
 
      <Box sx={{ p: 0.5, pb: 0, float: 'left' }}>
        <Button startIcon={<DownloadIcon />} onClick={handleExportExcel}>
          {name ? name : 'Export to Excel'}
        </Button>
      </Box>
    </>
  );
};
 
export default CustomDataGridToolbarServer;